import React, { useEffect, useRef, useState, useMemo } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Grid,
    IconButton,
    TextField,
    Typography,
    Card,
    CardContent,
    MenuItem,
    Stack,
    InputAdornment
} from '@mui/material';
import {
    Send as SendIcon,
    ArrowBack as ArrowBackIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    PersonSearch as PersonSearchIcon,
    Draw as DrawIcon,
    Clear as ClearIcon,
    ContentCopy as ContentCopyIcon,
    Mic as MicIcon,
    MicOff as MicOffIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { initializeDB } from '../../../config/db';
import { JOB_STORE } from '../../../config/constants';
import SignatureModalWithAuto from '../../common/SignatureModalWithAuto';
import SelectEmployeesAndTempsModal from '../../jha/SelectEmployeesAndTempsModal';
import { generateUUID } from '../../../utils/uuid';
import Swal from 'sweetalert2';
import { cableWiringScheduleService } from '../../../services/cableWiringScheduleService';
import axiosInstance from '../../../axiosInstance';
import useDictation from '../../../hooks/useDictation';

const STATUS_DRAFT = 'draft';
const STATUS_SUBMITTED = 'submitted';

// Color mapping for conductors
const CONDUCTOR_COLORS = {
    'BLK': '#000000',
    'RED': '#FF0000',
    'BLU': '#0000FF',
    'ORG': '#FF8000',
    'YEL': '#FFFF00',
    'BRN': '#8B4513',
    'RED/BLK': 'linear-gradient(90deg, #FF0000 50%, #000000 50%)',
    'BLU/BLK': 'linear-gradient(90deg, #0000FF 50%, #000000 50%)',
    'ORG/BLK': 'linear-gradient(90deg, #FF8000 50%, #000000 50%)',
    'YEL/BLK': 'linear-gradient(90deg, #FFFF00 50%, #000000 50%)',
    'BRN/BLK': 'linear-gradient(90deg, #8B4513 50%, #000000 50%)',
    'BLK/RED': 'linear-gradient(90deg, #000000 50%, #FF0000 50%)',
    'BLU/RED': 'linear-gradient(90deg, #0000FF 50%, #FF0000 50%)',
    'ORG/RED': 'linear-gradient(90deg, #FF8000 50%, #FF0000 50%)',
    'YEL/RED': 'linear-gradient(90deg, #FFFF00 50%, #FF0000 50%)',
    'BRN/RED': 'linear-gradient(90deg, #8B4513 50%, #FF0000 50%)',
    'BLK/BLU': 'linear-gradient(90deg, #000000 50%, #0000FF 50%)',
    'RED/BLU': 'linear-gradient(90deg, #FF0000 50%, #0000FF 50%)',
    'ORG/BLU': 'linear-gradient(90deg, #FF8000 50%, #0000FF 50%)',
    'YEL/BLU': 'linear-gradient(90deg, #FFFF00 50%, #0000FF 50%)',
    'BRN/BLU': 'linear-gradient(90deg, #8B4513 50%, #0000FF 50%)',
    'BLK/ORG': 'linear-gradient(90deg, #000000 50%, #FF8000 50%)',
    'RED/ORG': 'linear-gradient(90deg, #FF0000 50%, #FF8000 50%)',
    'BLU/ORG': 'linear-gradient(90deg, #0000FF 50%, #FF8000 50%)',
    'YEL/ORG': 'linear-gradient(90deg, #FFFF00 50%, #FF8000 50%)'
};

const CONDUCTOR_OPTIONS = [
    'BLK', 'RED', 'BLU', 'ORG', 'YEL', 'BRN',
    'RED/BLK', 'BLU/BLK', 'ORG/BLK', 'YEL/BLK', 'BRN/BLK',
    'BLK/RED', 'BLU/RED', 'ORG/RED', 'YEL/RED', 'BRN/RED',
    'BLK/BLU', 'RED/BLU', 'ORG/BLU', 'YEL/BLU', 'BRN/BLU',
    'BLK/ORG', 'RED/ORG', 'BLU/ORG', 'YEL/ORG'
];

const CableWiringScheduleForm = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const uuid = searchParams.get('uuid');

    const userFullName = localStorage.getItem('employeeFullName') || '';
    const userId = localStorage.getItem('employeeId') || '';

    const [jobs, setJobs] = useState([]);
    const [form, setForm] = useState({
        uuid: uuid || generateUUID(),
        jobCode: '',
        jobName: '',
        location: '',
        date: dayjs().format('YYYY-MM-DD'),
        cables: [{
            id: generateUUID(),
            cableNumber: '1',
            cableLevel: '',
            cableType: '',
            cableAWG: '',
            conductorsCount: 1,
            conductors: [''],
            comments: ''
        }],
        preparedBy: userFullName,
        preparedById: userId,
        signature: '',
        signatureDate: dayjs().format('YYYY-MM-DD'),
        status: STATUS_DRAFT
    });

    const [errors, setErrors] = useState({});
    const [jobs_loading, setJobsLoading] = useState(true);
    const [visibleCablesCount, setVisibleCablesCount] = useState(1);
    const [employeeModalOpen, setEmployeeModalOpen] = useState(false);
    const [signatureModalOpen, setSignatureModalOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [dictationCableIndex, setDictationCableIndex] = useState(null);
    const sigPadRef = useRef(null);
    const saveTimeoutRef = useRef(null);

    const {
        isListening,
        startDictation,
        stopDictation
    } = useDictation({
        onError: (error) => {
            console.error('Dictation error:', error);
        }
    });

    const loadJobs = async () => {
        try {
            const db = await initializeDB();
            const tx = db.transaction(JOB_STORE, 'readonly');
            const store = tx.objectStore(JOB_STORE);
            const allJobs = await store.getAll();
            setJobs(allJobs || []);
        } catch (error) {
            console.error('Error loading jobs:', error);
        } finally {
            setJobsLoading(false);
        }
    };

    const loadFormData = async () => {
        if (!uuid) return;
        
        try {
            const data = await cableWiringScheduleService.getFromIndexedDB(uuid);
            if (data) {
                // Show first cable immediately
                setVisibleCablesCount(1);
                setForm(data);
                setSubmitted(data.status === STATUS_SUBMITTED);
                
                // Then progressively show more cables
                if (data.cables && data.cables.length > 1) {
                    requestAnimationFrame(() => {
                        setVisibleCablesCount(Math.min(3, data.cables.length));
                        
                        setTimeout(() => {
                            setVisibleCablesCount(data.cables.length);
                        }, 50);
                    });
                }
            }
        } catch (error) {
            console.error('Error loading form data:', error);
        }
    };

    const loadedRef = useRef(false);
    
    useEffect(() => {
        if (loadedRef.current) {
            return;
        }
        
        loadJobs();
        if (uuid) {
            loadFormData();
        }
        loadedRef.current = true;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uuid]);

    useEffect(() => {
        return () => {
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }
        };
    }, []);

    const queueSave = (formData) => {
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }
        saveTimeoutRef.current = setTimeout(async () => {
            try {
                await cableWiringScheduleService.saveToIndexedDB(formData);
                
                // If no UUID in URL yet, add it after first save
                if (!uuid && formData.uuid) {
                    navigate(`/electrical/cable-wiring-schedule/form?uuid=${encodeURIComponent(formData.uuid)}`, { replace: true });
                }
            } catch (error) {
                console.error('Error saving to IndexedDB:', error);
            }
        }, 100);
    };

    const handleJobChange = (event, newValue) => {
        const updated = {
            ...form,
            jobCode: newValue ? (newValue.JobCode || newValue.Job || '') : '',
            jobName: newValue ? (newValue.JobName || '') : '',
            location: newValue ? (newValue.Location || '') : ''
        };
        setForm(updated);
        queueSave(updated);
    };

    const handleFieldChange = (field, value) => {
        const updated = { ...form, [field]: value };
        setForm(updated);
        queueSave(updated);
    };

    const getNextCableNumber = () => {
        if (form.cables.length === 0) return '1';
        const numbers = form.cables
            .map(c => parseInt(c.cableNumber) || 0)
            .filter(n => !isNaN(n));
        if (numbers.length === 0) return '1';
        return String(Math.max(...numbers) + 1);
    };

    const addCable = () => {
        const updated = {
            ...form,
            cables: [
                ...form.cables,
                {
                    id: generateUUID(),
                    cableNumber: getNextCableNumber(),
                    cableLevel: '',
                    cableType: '',
                    cableAWG: '',
                    conductorsCount: 1,
                    conductors: [''],
                    comments: ''
                }
            ]
        };
        setForm(updated);
        setVisibleCablesCount(updated.cables.length);
        queueSave(updated);
    };

    const duplicateCable = (index) => {
        const cableToCopy = form.cables[index];
        const newCable = {
            ...cableToCopy,
            id: generateUUID(),
            cableNumber: getNextCableNumber(),
            conductors: [...cableToCopy.conductors] // Deep copy array
        };
        const updated = {
            ...form,
            cables: [
                ...form.cables.slice(0, index + 1),
                newCable,
                ...form.cables.slice(index + 1)
            ]
        };
        setForm(updated);
        setVisibleCablesCount(updated.cables.length);
        queueSave(updated);
    };

    const removeCable = (index) => {
        if (form.cables.length === 1) {
            Swal.fire('Error', 'At least one cable is required', 'error');
            return;
        }
        const cables = [...form.cables];
        cables.splice(index, 1);
        const updated = { ...form, cables };
        setForm(updated);
        queueSave(updated);
    };

    const handleCableChange = (index, field, value) => {
        const cables = [...form.cables];
        cables[index][field] = value;

        // If conductors count changed, adjust conductors array
        if (field === 'conductorsCount') {
            const count = parseInt(value) || 1;
            const currentConductors = cables[index].conductors || [];
            if (count > currentConductors.length) {
                cables[index].conductors = [
                    ...currentConductors,
                    ...Array(count - currentConductors.length).fill('')
                ];
            } else {
                cables[index].conductors = currentConductors.slice(0, count);
            }
        }

        const updated = { ...form, cables };
        setForm(updated);
        queueSave(updated);
    };

    const handleConductorChange = (cableIndex, conductorIndex, value) => {
        const cables = [...form.cables];
        cables[cableIndex].conductors[conductorIndex] = value;
        const updated = { ...form, cables };
        setForm(updated);
        // Don't auto-save on every keystroke for conductors - only on blur
    };

    const handleSelectEmployee = (selectedArray) => {
        if (!selectedArray || selectedArray.length === 0) return;
        const person = selectedArray[0];
        const updated = {
            ...form,
            preparedBy: person.FullName || person.name || '',
            preparedById: person.id || person.employeeId || ''
        };
        setForm(updated);
        queueSave(updated);
        setEmployeeModalOpen(false);
    };

    const handleSignatureClick = () => {
        if (!form.preparedBy) {
            Swal.fire('Error', 'Please select who prepared this schedule', 'error');
            return;
        }
        setSignatureModalOpen(true);
    };

    const handleSignatureAccept = () => {
        if (!sigPadRef.current) {
            console.error('Signature pad ref not available');
            return;
        }

        const dataUrl = sigPadRef.current.toDataURL();
        const updated = {
            ...form,
            signature: dataUrl,
            signatureDate: dayjs().format('YYYY-MM-DD')
        };
        setForm(updated);
        queueSave(updated);
        setSignatureModalOpen(false);
    };

    const handleClearSignature = () => {
        const updated = { ...form, signature: '', signatureDate: '' };
        setForm(updated);
        queueSave(updated);
    };

    const handleStartDictation = (index) => {
        setDictationCableIndex(index);
        startDictation({
            onResult: (transcript) => {
                setForm(currentForm => {
                    const currentText = currentForm.cables[index]?.comments || '';
                    const needsSpace = currentText && !currentText.endsWith(' ');
                    const newText = currentText + (needsSpace ? ' ' : '') + transcript + ' ';
                    const updated = {
                        ...currentForm,
                        cables: currentForm.cables.map((cable, idx) =>
                            idx === index ? { ...cable, comments: newText } : cable
                        )
                    };
                    queueSave(updated);
                    return updated;
                });
            }
        });
    };

    const handleStopDictation = () => {
        stopDictation();
        setDictationCableIndex(null);
    };

    const validateForm = () => {
        const newErrors = {};
        
        if (!form.jobCode) newErrors.jobCode = 'Job is required';
        if (!form.date) newErrors.date = 'Date is required';
        
        form.cables.forEach((cable, index) => {
            if (!cable.cableNumber) newErrors[`cable_${index}_number`] = 'Cable Number required';
            if (!cable.cableLevel) newErrors[`cable_${index}_level`] = 'Cable Level required';
            if (!cable.cableType) newErrors[`cable_${index}_type`] = 'Cable Type required';
            if (!cable.cableAWG) newErrors[`cable_${index}_awg`] = 'Cable AWG required';
        });

        if (!form.preparedBy) newErrors.preparedBy = 'Prepared By is required';
        if (!form.signature) newErrors.signature = 'Signature is required';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            Swal.fire({
                icon: 'error',
                title: 'Validation Error',
                text: 'Please fill in all required fields correctly.'
            });
            return;
        }

        const result = await Swal.fire({
            icon: 'question',
            title: 'Submit Cable Wiring Schedule?',
            text: 'This will finalize the form and it cannot be edited after submission.',
            showCancelButton: true,
            confirmButtonText: 'Submit',
            cancelButtonText: 'Cancel'
        });

        if (!result.isConfirmed) return;

        setSaving(true);
        try {
            const submittedForm = {
                ...form,
                status: STATUS_SUBMITTED,
                date: form.date || dayjs().format('YYYY-MM-DD'),
                signatureDate: form.signatureDate || dayjs().format('YYYY-MM-DD'),
                updatedAt: new Date().toISOString()
            };

            // Save to IndexedDB
            await cableWiringScheduleService.saveToIndexedDB(submittedForm);
            
            // Submit to backend
            const response = await axiosInstance.post('/cable-wiring-schedule/submit', submittedForm);
            console.log('Server response:', response.data);

            setForm(submittedForm);
            setSubmitted(true);

            Swal.fire({
                icon: 'success',
                title: 'Submitted!',
                text: 'Cable Wiring Schedule has been submitted successfully.'
            });

            setTimeout(() => {
                navigate('/electrical/cable-wiring-schedule');
            }, 1500);

        } catch (error) {
            console.error('Submit error:', error);
            Swal.fire({
                icon: 'error',
                title: 'Submission Failed',
                text: error.response?.data?.message || 'Failed to submit form. Please try again.'
            });
        } finally {
            setSaving(false);
        }
    };

    const sortedJobs = useMemo(() => {
        const jobsWithLabels = jobs.map(job => ({
            ...job,
            Job: job.Job || job.JobCode,
            JobCode: job.JobCode || job.Job,
            label: `${job.Job || job.JobCode} - ${job.JobName}`,
            key: job.id || job.Job || job.JobCode
        }));
        return jobsWithLabels.sort((a, b) => {
            const jobA = parseInt(a.Job) || 0;
            const jobB = parseInt(b.Job) || 0;
            return jobB - jobA;
        });
    }, [jobs]);

    const selectedJob = sortedJobs.find(j => (j.Job === form.jobCode || j.JobCode === form.jobCode));

    return (
        <Box sx={{ height: 'calc(100vh - 60px)', display: 'flex', flexDirection: 'column', backgroundColor: '#f5f5f5' }}>
            {/* Header */}
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', backgroundColor: '#fff' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton onClick={() => navigate('/electrical/cable-wiring-schedule')}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                        Cable Wiring Schedule
                    </Typography>
                </Box>
            </Box>

            {/* Form Content */}
            <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                <Box sx={{ maxWidth: 900, mx: 'auto' }}>
                    {/* Header Information */}
                    <Card sx={{ p: 2, mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: 'primary.main' }}>
                            Header Information
                        </Typography>
                        <Grid container spacing={1.5}>
                            <Grid item xs={12} sm={6}>
                                <Autocomplete
                                    options={sortedJobs}
                                    getOptionLabel={(option) => option.label || ''}
                                    value={selectedJob || null}
                                    onChange={handleJobChange}
                                    loading={jobs_loading}
                                    size="small"
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Job *"
                                            size="small"
                                            error={!!errors.jobCode}
                                            helperText={errors.jobCode}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    type="date"
                                    label="Date"
                                    size="small"
                                    value={form.date}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        handleFieldChange('date', value);
                                    }}
                                    onClick={(e) => e.target.showPicker && e.target.showPicker()}
                                    InputLabelProps={{ shrink: true }}
                                    error={!!errors.date}
                                    helperText={errors.date}
                                />
                            </Grid>
                        </Grid>
                    </Card>

                    {/* Cables */}
                    <Card sx={{ p: 2, mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: 'primary.main' }}>
                            Cables
                        </Typography>

                        <Stack spacing={2}>
                            {form.cables.slice(0, visibleCablesCount).map((cable, cableIndex) => (
                        <Card key={cable.id} variant="outlined" sx={{ bgcolor: '#fafafa', p: 1.5 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                    Cable #{cableIndex + 1}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    <IconButton 
                                        size="small" 
                                        color="primary" 
                                        onClick={() => duplicateCable(cableIndex)}
                                        title="Duplicate cable"
                                    >
                                        <ContentCopyIcon fontSize="small" />
                                    </IconButton>
                                    {form.cables.length > 1 && (
                                        <IconButton 
                                            size="small" 
                                            color="error" 
                                            onClick={() => removeCable(cableIndex)}
                                            title="Delete cable"
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                </Box>
                            </Stack>

                            <Grid container spacing={1.5}>
                            <Grid item xs={12} sm={2.4}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Cable Number *"
                                    value={cable.cableNumber}
                                    onChange={(e) => handleCableChange(cableIndex, 'cableNumber', e.target.value)}
                                    error={!!errors[`cable_${cableIndex}_number`]}
                                    inputProps={{ inputMode: 'numeric' }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={2.4}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Cable Level *"
                                    value={cable.cableLevel}
                                    onChange={(e) => handleCableChange(cableIndex, 'cableLevel', e.target.value)}
                                    error={!!errors[`cable_${cableIndex}_level`]}
                                    inputProps={{ inputMode: 'numeric' }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={2.4}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Cable Type *"
                                    value={cable.cableType}
                                    onChange={(e) => handleCableChange(cableIndex, 'cableType', e.target.value)}
                                    error={!!errors[`cable_${cableIndex}_type`]}
                                    inputProps={{ inputMode: 'text' }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={2.4}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Cable AWG *"
                                    value={cable.cableAWG}
                                    onChange={(e) => handleCableChange(cableIndex, 'cableAWG', e.target.value)}
                                    error={!!errors[`cable_${cableIndex}_awg`]}
                                    inputProps={{ inputMode: 'numeric' }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={2.4}>
                                <TextField
                                    fullWidth
                                    select
                                    size="small"
                                    label="Conductors"
                                    value={cable.conductorsCount}
                                    onChange={(e) => handleCableChange(cableIndex, 'conductorsCount', e.target.value)}
                                >
                                    {[...Array(25)].map((_, i) => (
                                        <MenuItem key={i + 1} value={i + 1}>
                                            {i + 1}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            {/* Conductor Inputs */}
                            <Grid item xs={12}>
                                <Box sx={{ 
                                    display: 'grid', 
                                    gridTemplateColumns: 'repeat(6, 1fr)', 
                                    gap: 1.5,
                                    mt: 1
                                }}>
                                    {cable.conductors.map((conductor, conductorIndex) => {
                                        const conductorLabel = CONDUCTOR_OPTIONS[conductorIndex] || `#${conductorIndex + 1}`;
                                        const conductorColor = CONDUCTOR_COLORS[conductorLabel];
                                        
                                        return (
                                            <Box key={conductorIndex}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    label={
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <Box
                                                                sx={{
                                                                    width: 12,
                                                                    height: 12,
                                                                    borderRadius: '50%',
                                                                    background: conductorColor || '#999',
                                                                    border: '1px solid #666',
                                                                    flexShrink: 0
                                                                }}
                                                            />
                                                            {conductorLabel}
                                                        </Box>
                                                    }
                                                    value={conductor}
                                                    onChange={(e) => handleConductorChange(cableIndex, conductorIndex, e.target.value)}
                                                    onBlur={() => queueSave(form)}
                                                    inputProps={{ inputMode: 'text' }}
                                                    InputLabelProps={{ shrink: true }}
                                                />
                                    </Box>
                                );
                            })}
                        </Box>
                    </Grid>

                            {/* Comments */}
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={2}
                                    size="small"
                                    label="Comments"
                                    value={cable.comments || ''}
                                    onChange={(e) => handleCableChange(cableIndex, 'comments', e.target.value)}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 0.5 }}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => {
                                                        if (isListening && dictationCableIndex === cableIndex) {
                                                            handleStopDictation();
                                                        } else {
                                                            if (isListening) handleStopDictation();
                                                            handleStartDictation(cableIndex);
                                                        }
                                                    }}
                                                    color={isListening && dictationCableIndex === cableIndex ? 'error' : 'primary'}
                                                >
                                                    {isListening && dictationCableIndex === cableIndex ? 
                                                        <MicOffIcon fontSize="small" /> : 
                                                        <MicIcon fontSize="small" />
                                                    }
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            </Grid>
                </Grid>
            </Card>
        ))}
                            
                            {visibleCablesCount < form.cables.length && (
                                <Card variant="outlined" sx={{ bgcolor: '#f0f0f0', p: 2, textAlign: 'center' }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Loading {form.cables.length - visibleCablesCount} more cables...
                                    </Typography>
                                </Card>
                            )}
                            
                            <Box display="flex" justifyContent="flex-end" mt={1.5}>
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={addCable}
                                    size="small"
                                >
                                    Add Cable
                                </Button>
                            </Box>
                        </Stack>
                    </Card>

                    {/* Signature */}
                    <Card sx={{ p: 2, mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: 'primary.main' }}>
                            Prepared By
                        </Typography>
                    
                        <Grid container spacing={1.5} alignItems="center">
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Name *"
                                    value={form.preparedBy}
                                    InputProps={{
                                        readOnly: true,
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setEmployeeModalOpen(true);
                                                    }}
                                                    edge="end"
                                                >
                                                    <PersonSearchIcon fontSize="small" />
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                        style: { cursor: 'pointer' }
                                    }}
                                    onClick={() => setEmployeeModalOpen(true)}
                                    error={!!errors.preparedBy}
                                    helperText={errors.preparedBy}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="date"
                                    label="Date"
                                    value={form.signatureDate}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        handleFieldChange('signatureDate', value);
                                    }}
                                    InputLabelProps={{ shrink: true }}
                                    onClick={(e) => {
                                        e.currentTarget.querySelector('input')?.showPicker?.();
                                    }}
                                    InputProps={{
                                        style: { cursor: 'pointer' }
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Box display="flex" justifyContent="center" alignItems="center" minHeight={60}>
                                    {form.signature ? (
                                        <Box position="relative" display="inline-block">
                                            <img
                                                src={form.signature}
                                                alt="Signature"
                                                style={{
                                                    maxWidth: 200,
                                                    height: 60,
                                                    border: '1px solid #ddd',
                                                    borderRadius: 4,
                                                    cursor: 'pointer'
                                                }}
                                                onClick={() => handleSignatureClick()}
                                                title="Click to edit signature"
                                            />
                                            <IconButton
                                                size="small"
                                                onClick={handleClearSignature}
                                                sx={{ 
                                                    position: 'absolute', 
                                                    top: -8, 
                                                    right: -8, 
                                                    bgcolor: 'error.main', 
                                                    color: 'white', 
                                                    '&:hover': { bgcolor: 'error.dark' } 
                                                }}
                                            >
                                                <ClearIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Box>
                                    ) : (
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<DrawIcon />}
                                            onClick={handleSignatureClick}
                                            sx={{ minWidth: 120 }}
                                        >
                                            Sign
                                        </Button>
                                    )}
                                </Box>
                                {errors.signature && (
                                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block', textAlign: 'center' }}>
                                        {errors.signature}
                                    </Typography>
                                )}
                            </Grid>
                        </Grid>
                    </Card>

                    {/* Submit */}
                    <Box display="flex" justifyContent="center" mt={2} mb={2}>
                        <Button
                            variant="contained"
                            color="primary"
                            size="large"
                            startIcon={<SendIcon />}
                            onClick={handleSubmit}
                            disabled={saving}
                            sx={{ minWidth: 200 }}
                        >
                            {saving ? 'Submitting...' : 'Submit'}
                        </Button>
                    </Box>
                </Box>
            </Box>


            {/* Modals */}
            <SelectEmployeesAndTempsModal
                open={employeeModalOpen}
                onClose={() => setEmployeeModalOpen(false)}
                onSelect={handleSelectEmployee}
                singleSelect={true}
                alreadySelected={form.preparedBy ? [{ FullName: form.preparedBy }] : []}
            />

            <SignatureModalWithAuto
                open={signatureModalOpen}
                onClose={() => setSignatureModalOpen(false)}
                sigPadRef={sigPadRef}
                employeeName={form.preparedBy || ''}
                onAccept={handleSignatureAccept}
                existingSignature={form.signature}
            />
        </Box>
    );
};

export default CableWiringScheduleForm;

